import { cdnBase, mediaHashes } from 'virtual:media-cdn'

/**
 * 素材地址统一出口。
 * 配置里写的是 ./media/xxx（public 目录下的相对路径），这里换成最终地址：
 * - 开启 CDN：https://static.rarecard.cn/media/xxx?v=哈希
 * - 未开启：./media/xxx?v=哈希
 * 开关和 CDN 域名在 public/content/site.json 的 cdn 节点，构建时生效（见 vite.config.ts）。
 */
const LOCAL_MEDIA = /^\.\/(media\/[^?#]+)$/

export function mediaUrl(value: string): string {
  const match = LOCAL_MEDIA.exec(value)
  if (!match) return value
  // 直接双击打开的离线单文件版，素材只能读本地
  if (typeof window !== 'undefined' && window.location.protocol === 'file:') return value
  const key = match[1]
  const query = mediaHashes[key] ? `?v=${mediaHashes[key]}` : ''
  return cdnBase ? `${cdnBase}/${key}${query}` : `./${key}${query}`
}

/** 把配置对象里所有 ./media/ 开头的字符串换成最终地址（返回新对象，不改原对象） */
export function rewriteMedia<T>(value: T): T {
  if (typeof value === 'string') return mediaUrl(value) as T
  if (Array.isArray(value)) return value.map((item) => rewriteMedia(item)) as T
  if (value && typeof value === 'object') {
    const output: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) output[key] = rewriteMedia(item)
    return output as T
  }
  return value
}

/**
 * CDN 兜底：某张图或视频从 CDN 加载失败（CDN 故障、域名解析异常等），自动改从本服务器加载。
 * 只覆盖 <img> 和 <video>；CSS 背景图（精灵动图）没有失败事件，不在兜底范围内。
 */
export function installCdnFallback(): void {
  if (!cdnBase || typeof window === 'undefined') return
  const prefix = `${cdnBase}/`
  window.addEventListener('error', (event) => {
    const element = event.target
    if (!(element instanceof HTMLImageElement || element instanceof HTMLVideoElement)) return
    const src = element.getAttribute('src')
    if (!src?.startsWith(prefix)) return
    const local = `./${src.slice(prefix.length)}`
    console.warn('[CDN] 素材加载失败，改从本服务器加载：', src)
    element.setAttribute('src', local)
    if (element instanceof HTMLVideoElement) {
      const poster = element.getAttribute('poster')
      if (poster?.startsWith(prefix)) element.setAttribute('poster', `./${poster.slice(prefix.length)}`)
    }
  }, true)
}
